/* =============================================================================
   VIEWS / ADMIN / INGREDIENTS-ADMIN — Zutaten-Verwaltung (S151 ERP Phase A1)
   =============================================================================
   CRUD für den Zutaten-Stamm hinter Feature-Flag `ingredientsDb`. Liste +
   Modal-Formular: Name, Kategorie/Unterkat., Basis-Einheit, Nährwerte je
   100 g/ml, Allergene (EU-14), Diät-Tags, benannte Einheiten (Sub-Liste),
   Einkauf + Verlustfaktoren (eingeklappt). Nährwerte beziehen sich auf die
   Migration phase_113; Aggregation/Anzeige je Gericht via core/nutrition.js.

   Non-destruktive Renders (Memory): das Modal-Formular bindet an einen Draft
   (kein Re-Render bei Tastendruck); nur die Einheiten-Sub-Liste rendert ihren
   eigenen Container neu. Inline-Handler ohne window.func() (CSP/dom-bindings).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { ALLERGENS_EU14, DIETARY_TAGS } from '../../core/nutrition.js';
import { costPerBase, formatEuro } from '../../core/food-cost.js';
import { rawPerBaseFromSupplier, effectivePurchaseFromSuppliers, primarySupplier, omitDerivedPurchase } from '../../core/suppliers.js';
import { formatStock } from '../../core/stock.js';

const CATEGORIES = ['Fleisch', 'Fisch', 'Käse', 'Brot', 'Soße', 'Gemüse', 'Obst', 'Getränk', 'Gewürz', 'Sonstiges'];
const BASE_UNITS = [
  { v: 'g', label: 'Gramm (g)' },
  { v: 'ml', label: 'Milliliter (ml)' },
  { v: 'piece', label: 'Stück' }
];
// Nährwert-Felder (camelCase, je 100 g/ml) — Reihenfolge = Anzeige.
const NUTRIENTS = [
  { k: 'energyKcal', label: 'Energie', unit: 'kcal' },
  { k: 'energyKj', label: 'Energie', unit: 'kJ' },
  { k: 'fatG', label: 'Fett', unit: 'g' },
  { k: 'fatSaturatedG', label: '… ges. Fettsäuren', unit: 'g' },
  { k: 'carbsG', label: 'Kohlenhydrate', unit: 'g' },
  { k: 'sugarG', label: '… Zucker', unit: 'g' },
  { k: 'proteinG', label: 'Eiweiß', unit: 'g' },
  { k: 'saltG', label: 'Salz', unit: 'g' }
];

let _cache = [];          // geladene Zutaten (camelCase)
let _draft = null;        // aktuelle Bearbeitung
let _draftUnits = [];     // benannte Einheiten des Drafts (im UI editiert)
let _draftSuppliers = []; // E4a — ingredient_suppliers der bearbeiteten Zutat (live persistiert)
let _allSuppliers = [];   // E4a — Lieferanten-Stamm für das Dropdown
let _erpInfo = {};        // S166 — ingredient_overview() je Zutat (Bestand + Hauptlieferant)

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

// -----------------------------------------------------------------------------
// Tab-Render
// -----------------------------------------------------------------------------
export async function renderIngredientsAdmin() {
  const el = document.getElementById('tab-ingredients');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Die Zutaten-Datenbank ist deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Zutaten…</p></div>`;
  try {
    _cache = await storage().ingredients.list();
  } catch (e) {
    console.warn('[ingredients] list:', e);
    _cache = [];
  }
  // S166 — Bestand + Hauptlieferant je Zutat (1 Roundtrip, read-only Infozeile).
  try {
    const ov = await storage().ingredients.overview();
    _erpInfo = Object.fromEntries((ov || []).map(o => [o.ingredientId, o]));
  } catch (e) {
    console.warn('[ingredients] overview:', e);
    _erpInfo = {};
  }
  el.innerHTML = renderListHtml(_cache);
}

// S166 — Bestand + Hauptlieferant (read-only) für die Zutaten-Zeile.
function erpInfoLine(it) {
  const info = _erpInfo[it.id];
  if (!info) return '';
  const stock = `Bestand ${formatStock(info.stockBase, it.baseUnit)}`;
  const sup = info.primarySupplierName
    ? ` · Lieferant ${htmlEscape(info.primarySupplierName)}`
    : ' · <span style="color:var(--ink-soft,#999);">kein Hauptlieferant</span>';
  return `<div class="hint" style="font-size:.76rem; margin-top:2px;">${stock}${sup}</div>`;
}

function renderListHtml(items) {
  const rows = items.map(renderRowHtml).join('');
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Zutaten-Datenbank</h2>
          <p class="hint" style="margin:4px 0 0;">Atome für Nährwerte, Allergene und Food-Cost (€/Einheit aus Einkauf + Ausbeute). ${items.length} Zutat(en).</p>
        </div>
        <button class="btn btn-primary btn-sm" onclick="openIngredientForm()">+ Zutat anlegen</button>
      </div>
      <div class="ingredient-list" style="margin-top:14px; display:flex; flex-direction:column; gap:6px;">
        ${rows || '<div class="hint" style="padding:16px; text-align:center;">Noch keine Zutaten. Lege die erste an.</div>'}
      </div>
    </div>`;
}

// S158 — €/Basiseinheit (lokal aus Einkauf+Yield, Spiegel ingredient_cost_per_base).
// g/ml → „€/100 g|ml" (handlicher als €/g), Stück → „€/Stück". Fehlt Einkauf → „—".
function pricePerUnitLabel(it) {
  const cpb = costPerBase(it);
  if (cpb == null) return '€/Einheit: <span style="color:var(--danger,#b94a48);">— unvollständig</span>';
  if (it.baseUnit === 'piece') return `€/Stück: <strong>${formatEuro(cpb)}</strong>`;
  const per = it.baseUnit === 'ml' ? '100 ml' : '100 g';
  return `€/${per}: <strong>${formatEuro(cpb * 100)}</strong>`;
}

function renderRowHtml(it) {
  const kcal = it.energyKcal != null ? `${it.energyKcal} kcal` : '—';
  const unit = BASE_UNITS.find(u => u.v === it.baseUnit);
  const al = (it.allergens || []).length ? `· ${(it.allergens || []).length} Allergen(e)` : '';
  return `
    <div class="ingredient-row" data-ingredient="${htmlEscape(it.id)}"
         style="display:grid; grid-template-columns:1fr auto auto auto; gap:12px; align-items:center; padding:10px 14px; background:var(--bg-soft,#f6f6f6); border:1px solid var(--border,#e2e2e2); border-radius:8px;">
      <div style="min-width:0;">
        <div style="font-weight:600;">${htmlEscape(it.name)}</div>
        <div class="hint" style="font-size:.8rem;">${htmlEscape(it.category || '—')}${it.subcategory ? ' / ' + htmlEscape(it.subcategory) : ''} · ${htmlEscape(unit ? unit.label : it.baseUnit)} · ${kcal} /100 ${al}</div>
        ${erpInfoLine(it)}
      </div>
      <div class="hint" style="font-size:.82rem; white-space:nowrap;">${pricePerUnitLabel(it)}</div>
      <button class="btn btn-ghost btn-sm" onclick="openIngredientForm('${htmlEscape(it.id)}')">Bearbeiten</button>
      <button class="btn btn-ghost btn-sm" style="color:var(--danger,#b94a48);" onclick="deleteIngredient('${htmlEscape(it.id)}')" title="Löschen">✕</button>
    </div>`;
}

// -----------------------------------------------------------------------------
// Formular (Modal, Draft-gebunden)
// -----------------------------------------------------------------------------
export function openIngredientForm(id) {
  const existing = id ? _cache.find(x => x.id === id) : null;
  _draft = existing
    ? JSON.parse(JSON.stringify(existing))
    : { name: '', category: '', subcategory: '', baseUnit: 'g', allergens: [], dietaryTags: [] };
  _draftUnits = [];
  _draftSuppliers = [];
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = renderFormHtml(_draft, !!existing);
  overlay.classList.add('active');
  // benannte Einheiten + Lieferanten async laden (nur bei Bestand)
  if (existing) {
    storage().ingredientUnits.listFor(id).then(units => {
      _draftUnits = units || [];
      renderUnitsList();
    }).catch(() => {});
    loadSuppliersSection(id);
  } else {
    renderUnitsList();
    renderSuppliersSection(); // zeigt „erst speichern"-Hinweis
  }
}

// E4a — Lieferanten der Zutat + Lieferanten-Stamm laden, dann Abschnitt rendern.
async function loadSuppliersSection(ingredientId) {
  try {
    const [links, all] = await Promise.all([
      storage().ingredientSuppliers.listFor(ingredientId),
      storage().suppliers.overview()
    ]);
    _draftSuppliers = links || [];
    _allSuppliers = (all || []).filter(s => s.active);
  } catch (e) {
    console.warn('[ingredients] suppliers load:', e);
    _draftSuppliers = []; _allSuppliers = [];
  }
  renderSuppliersSection();
}

function renderFormHtml(d, isEdit) {
  return `
    <div class="modal-header">
      <h2>${isEdit ? 'Zutat bearbeiten' : 'Neue Zutat'}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field-row">
      <div class="field"><label>Name *</label>
        <input id="ing-name" type="text" value="${htmlEscape(d.name || '')}" oninput="ingredientDraftSet('name', this.value)"></div>
      <div class="field"><label>Basis-Einheit *</label>
        <select id="ing-base" onchange="ingredientDraftSetBaseUnit(this.value)">
          ${BASE_UNITS.map(u => `<option value="${u.v}" ${d.baseUnit === u.v ? 'selected' : ''}>${u.label}</option>`).join('')}
        </select></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Kategorie</label>
        <input id="ing-cat" type="text" list="ing-cat-list" value="${htmlEscape(d.category || '')}" oninput="ingredientDraftSet('category', this.value)">
        <datalist id="ing-cat-list">${CATEGORIES.map(c => `<option value="${c}">`).join('')}</datalist></div>
      <div class="field"><label>Unterkategorie</label>
        <input id="ing-sub" type="text" value="${htmlEscape(d.subcategory || '')}" oninput="ingredientDraftSet('subcategory', this.value)"></div>
    </div>

    <div id="ing-piece-row" class="field" style="${d.baseUnit === 'piece' ? '' : 'display:none;'}">
      <label>Gewicht 1 Stück (g) <small>— nötig für Nährwert-Rechnung bei „Stück"</small></label>
      <input id="ing-piece" type="number" step="0.1" min="0" value="${d.pieceWeightG != null ? d.pieceWeightG : ''}" oninput="ingredientDraftSetNum('pieceWeightG', this.value)">
    </div>

    <h3 style="margin:14px 0 6px; font-size:1rem;">Nährwerte je 100 g/ml <small style="font-weight:normal; color:var(--ink-soft);">(verifizierte Quelle eintragen)</small></h3>
    <div style="display:grid; grid-template-columns:repeat(2,1fr); gap:8px 14px;">
      ${NUTRIENTS.map(n => `
        <div class="field" style="margin:0;"><label style="font-size:.82rem;">${n.label} (${n.unit})</label>
          <input type="number" step="0.1" min="0" value="${d[n.k] != null ? d[n.k] : ''}" oninput="ingredientDraftSetNum('${n.k}', this.value)"></div>
      `).join('')}
    </div>

    <h3 style="margin:14px 0 6px; font-size:1rem;">Allergene (EU-14)</h3>
    <div class="med-tag-list" style="display:flex; flex-wrap:wrap; gap:6px;">
      ${ALLERGENS_EU14.map(a => `
        <label class="med-tag-chip ${(d.allergens || []).includes(a.key) ? 'active' : ''}">
          <input type="checkbox" ${(d.allergens || []).includes(a.key) ? 'checked' : ''} onchange="ingredientDraftToggle('allergens','${a.key}', this.checked)"> ${htmlEscape(a.label)}
        </label>`).join('')}
    </div>

    <h3 style="margin:14px 0 6px; font-size:1rem;">Diät-Tags <small style="font-weight:normal; color:var(--ink-soft);">(Vorschlag — Mensch bestätigt, LMIV)</small></h3>
    <div class="med-tag-list" style="display:flex; flex-wrap:wrap; gap:6px;">
      ${DIETARY_TAGS.map(t => `
        <label class="med-tag-chip ${(d.dietaryTags || []).includes(t.key) ? 'active' : ''}">
          <input type="checkbox" ${(d.dietaryTags || []).includes(t.key) ? 'checked' : ''} onchange="ingredientDraftToggle('dietaryTags','${t.key}', this.checked)"> ${htmlEscape(t.label)}
        </label>`).join('')}
    </div>

    <h3 style="margin:14px 0 6px; font-size:1rem;">Benannte Einheiten <small style="font-weight:normal; color:var(--ink-soft);">(1 Einheit = X Basis, z. B. 1 Scheibe = 20 g)</small></h3>
    <div id="ing-units-list"></div>
    <div class="field-row" style="align-items:flex-end;">
      <div class="field"><label style="font-size:.82rem;">Name</label><input id="ing-unit-name" type="text" placeholder="Scheibe"></div>
      <div class="field"><label style="font-size:.82rem;">= X Basis</label><input id="ing-unit-factor" type="number" step="0.01" min="0" placeholder="20"></div>
      <button class="btn btn-secondary btn-sm" onclick="ingredientAddUnit()">+ Einheit</button>
    </div>

    <h3 style="margin:14px 0 6px; font-size:1rem;">Lieferanten <small style="font-weight:normal; color:var(--ink-soft);">(Hauptlieferant speist Einkaufspreis &amp; Gebinde → Food-Cost/Lagerwert)</small></h3>
    <div id="ing-suppliers-section"></div>

    <details style="margin-top:10px;">
      <summary style="cursor:pointer; font-weight:600;">Einkauf & Verlustfaktoren <small style="font-weight:normal; color:var(--ink-soft);">(fließt in Food-Cost ein — €/Einheit = Preis ÷ Menge ÷ Ausbeute)</small></summary>
      <p id="ing-purchase-note" class="hint" style="font-size:.8rem; margin:6px 0 0;">Hinweis: Bei gesetztem <strong>Hauptlieferant</strong> werden Einkaufspreis &amp; Gebinde-Menge von dort gespeist (oben im Abschnitt „Lieferanten" ändern). Ohne Lieferant gilt die manuelle Eingabe hier.</p>
      <div class="field-row" style="margin-top:8px;">
        <div class="field"><label>Einkaufspreis (€ je Gebinde)</label><input id="ing-pp" type="number" step="0.01" min="0" value="${d.purchasePrice != null ? d.purchasePrice : ''}" oninput="ingredientDraftSetNum('purchasePrice', this.value)"></div>
        <div class="field"><label>Gebinde-Menge (Basis)</label><input id="ing-pq" type="number" step="0.01" min="0" value="${d.purchaseQty != null ? d.purchaseQty : ''}" oninput="ingredientDraftSetNum('purchaseQty', this.value)"></div>
      </div>
      <div class="field-row">
        <div class="field"><label>Rüst-Ausbeute (0–1)</label><input type="number" step="0.01" min="0" max="1" value="${d.yieldCleaning != null ? d.yieldCleaning : ''}" oninput="ingredientDraftSetNum('yieldCleaning', this.value)"></div>
        <div class="field"><label>Gar-Ausbeute (0–1)</label><input type="number" step="0.01" min="0" max="1" value="${d.yieldCooking != null ? d.yieldCooking : ''}" oninput="ingredientDraftSetNum('yieldCooking', this.value)"></div>
      </div>
      <div class="field"><label>Quelle Verlustfaktor</label><input type="text" placeholder="z. B. BLS / DGE" value="${htmlEscape(d.yieldSource || '')}" oninput="ingredientDraftSet('yieldSource', this.value)"></div>
    </details>

    <div class="btn-row" style="margin-top:16px;">
      <button class="btn btn-primary" onclick="saveIngredient()">Speichern</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
}

function renderUnitsList() {
  const wrap = document.getElementById('ing-units-list');
  if (!wrap) return;
  if (!_draftUnits.length) {
    wrap.innerHTML = `<div class="hint" style="font-size:.82rem; padding:4px 0;">Noch keine benannten Einheiten.</div>`;
    return;
  }
  wrap.innerHTML = _draftUnits.map((u, i) => `
    <div style="display:flex; align-items:center; gap:8px; padding:3px 0;">
      <span style="font-weight:600;">${htmlEscape(u.unitName)}</span>
      <span class="hint" style="font-size:.82rem;">= ${htmlEscape(String(u.gramsOrMl))} Basis</span>
      <button class="btn btn-ghost btn-xs" style="color:var(--danger,#b94a48);" onclick="ingredientRemoveUnit(${i})">entfernen</button>
    </div>`).join('');
}

// -----------------------------------------------------------------------------
// E4a — Lieferanten-Abschnitt im Zutaten-Editor (live persistiert, non-destruktiv:
// rendert nur den eigenen Container neu)
// -----------------------------------------------------------------------------
function effectivePriceHint() {
  const eff = effectivePurchaseFromSuppliers(_draftSuppliers);
  const primary = _draftSuppliers.find(s => s.isPrimary);
  if (!eff || !primary) return '<span class="hint" style="font-size:.82rem;">Kein Hauptlieferant — Einkaufspreis kommt aus der manuellen Eingabe unten.</span>';
  const rpb = rawPerBaseFromSupplier(primary);
  let priceLbl = '—';
  if (rpb != null) {
    priceLbl = _draft.baseUnit === 'piece' ? `${formatEuro(rpb)}/Stück`
      : `${formatEuro(rpb * 100)}/${_draft.baseUnit === 'ml' ? '100 ml' : '100 g'}`;
  }
  return `<span class="hint" style="font-size:.82rem;">Hauptlieferant <strong>${htmlEscape(primary.supplierName || '—')}</strong> → effektiv <strong>${priceLbl}</strong> (speist Food-Cost &amp; Lagerwert).</span>`;
}

function renderSuppliersSection() {
  const wrap = document.getElementById('ing-suppliers-section');
  if (!wrap) return;
  if (!_draft || !_draft.id) {
    wrap.innerHTML = `<div class="hint" style="font-size:.82rem; padding:4px 0;">Bitte die Zutat zuerst speichern, dann hier Lieferanten zuordnen.</div>`;
    return;
  }
  const rows = _draftSuppliers.map(renderSupplierLinkHtml).join('');
  const supplierOpts = _allSuppliers.length
    ? _allSuppliers.map(s => `<option value="${htmlEscape(s.id)}">${htmlEscape(s.name)}</option>`).join('')
    : '';
  const baseLbl = _draft.baseUnit === 'piece' ? 'Stück' : (_draft.baseUnit || 'Basis');
  const addForm = _allSuppliers.length ? `
    <div style="margin-top:8px; padding:10px; border:1px dashed var(--border,#ccc); border-radius:8px;">
      <div class="field-row" style="align-items:flex-end; flex-wrap:wrap;">
        <div class="field" style="min-width:150px;"><label style="font-size:.8rem;">Lieferant</label>
          <select id="isup-supplier">${supplierOpts}</select></div>
        <div class="field" style="max-width:120px;"><label style="font-size:.8rem;">Preis (€ je Einheit)</label>
          <input id="isup-price" type="number" step="0.01" min="0" placeholder="12.00"></div>
        <div class="field" style="max-width:120px;"><label style="font-size:.8rem;">Einkaufseinheit</label>
          <input id="isup-unit" type="text" placeholder="Karton"></div>
        <div class="field" style="max-width:130px;"><label style="font-size:.8rem;">= X ${htmlEscape(baseLbl)}</label>
          <input id="isup-qty" type="number" step="0.01" min="0" placeholder="6000"></div>
        <div class="field" style="max-width:120px;"><label style="font-size:.8rem;">Artikel-Nr.</label>
          <input id="isup-sku" type="text" placeholder="optional"></div>
        <button class="btn btn-secondary btn-sm" onclick="ingredientSupplierAdd()">+ Lieferant</button>
      </div>
    </div>` : `<div class="hint" style="font-size:.82rem; padding:4px 0;">Noch keine Lieferanten im Stamm. Erst im Tab „Lieferanten" anlegen.</div>`;
  wrap.innerHTML = `
    <div style="margin-bottom:6px;">${effectivePriceHint()}</div>
    <div style="display:flex; flex-direction:column; gap:4px;">${rows || '<div class="hint" style="font-size:.82rem;">Noch keine Lieferanten zugeordnet.</div>'}</div>
    ${addForm}`;
  applyPurchaseLock();
}

// E4a-Hotfix — bei gesetztem Hauptlieferant ist die manuelle Einkauf-Sektion abgeleitet:
// Felder read-only + auf den Haupt-Wert gesetzt (Anzeige), und beim Speichern werden
// purchase_* ausgeschlossen (omitDerivedPurchase). Ohne Haupt: editierbar (Fallback).
function applyPurchaseLock() {
  const pp = document.getElementById('ing-pp');
  const pq = document.getElementById('ing-pq');
  const note = document.getElementById('ing-purchase-note');
  if (!pp || !pq) return;
  const eff = effectivePurchaseFromSuppliers(_draftSuppliers);
  const primary = primarySupplier(_draftSuppliers);
  if (eff && primary) {
    pp.value = eff.purchasePrice != null ? eff.purchasePrice : '';
    pq.value = eff.purchaseQty != null ? eff.purchaseQty : '';
    pp.disabled = true; pq.disabled = true;
    pp.style.opacity = pq.style.opacity = '.6';
    // Draft an den Haupt angleichen, damit kein veralteter Wert hängenbleibt (wird beim
    // Speichern ohnehin ausgeschlossen, aber so ist die Anzeige konsistent).
    _draft.purchasePrice = eff.purchasePrice;
    _draft.purchaseQty = eff.purchaseQty;
    _draft.purchaseUnit = eff.purchaseUnit;
    if (note) note.innerHTML = `Einkaufspreis &amp; Gebinde kommen vom <strong>Hauptlieferant ${htmlEscape(primary.supplierName || '')}</strong> (oben ändern) — manuelle Eingabe hier deaktiviert.`;
  } else {
    pp.disabled = false; pq.disabled = false;
    pp.style.opacity = pq.style.opacity = '';
    if (note) note.innerHTML = `Hinweis: Bei gesetztem <strong>Hauptlieferant</strong> werden Einkaufspreis &amp; Gebinde-Menge von dort gespeist (oben im Abschnitt „Lieferanten" ändern). Ohne Lieferant gilt die manuelle Eingabe hier.`;
  }
}

function renderSupplierLinkHtml(x) {
  const rpb = rawPerBaseFromSupplier(x);
  const unitLbl = x.purchaseUnit ? `${formatEuro(x.purchasePrice)} / ${htmlEscape(x.purchaseUnit)}` : (x.purchasePrice != null ? formatEuro(x.purchasePrice) : '—');
  const conv = (x.unitQtyBase != null && x.purchaseUnit) ? ` <span class="hint" style="font-size:.74rem;">(1 ${htmlEscape(x.purchaseUnit)} = ${htmlEscape(String(x.unitQtyBase))} Basis${rpb != null ? ' · ' + formatEuro(rpb * (_draft.baseUnit === 'piece' ? 1 : 100)) + '/' + (_draft.baseUnit === 'piece' ? 'Stk' : (_draft.baseUnit === 'ml' ? '100ml' : '100g')) : ''})</span>` : '';
  const sku = x.supplierSku ? ` <span class="hint" style="font-size:.74rem;">Art-Nr. ${htmlEscape(x.supplierSku)}</span>` : '';
  return `
    <div style="display:flex; align-items:center; gap:8px; padding:4px 0; flex-wrap:wrap;">
      <label style="display:inline-flex; align-items:center; gap:4px; font-size:.84rem; cursor:pointer;">
        <input type="radio" name="isup-primary" ${x.isPrimary ? 'checked' : ''} onchange="ingredientSupplierSetPrimary('${htmlEscape(x.id)}')"> Haupt
      </label>
      <span style="font-weight:600;">${htmlEscape(x.supplierName || '—')}</span>
      <span class="hint" style="font-size:.82rem;">${unitLbl}</span>${conv}${sku}
      <button class="btn btn-ghost btn-xs" style="color:var(--danger,#b94a48); margin-left:auto;" onclick="ingredientSupplierRemove('${htmlEscape(x.id)}')">entfernen</button>
    </div>`;
}

export async function ingredientSupplierAdd() {
  if (!_draft || !_draft.id) return;
  const sup = document.getElementById('isup-supplier');
  const price = document.getElementById('isup-price');
  const unit = document.getElementById('isup-unit');
  const qty = document.getElementById('isup-qty');
  const sku = document.getElementById('isup-sku');
  const supplierId = sup && sup.value;
  if (!supplierId) { showToast('Bitte einen Lieferanten wählen', 'error'); return; }
  const unitQty = qty && qty.value !== '' ? Number(qty.value) : null;
  if (unitQty != null && (!isFinite(unitQty) || unitQty <= 0)) { showToast('Umrechnung muss > 0 sein', 'error'); return; }
  try {
    await storage().ingredientSuppliers.upsert({
      ingredientId: _draft.id, supplierId,
      purchasePrice: price && price.value !== '' ? Number(price.value) : null,
      purchaseUnit: (unit && unit.value || '').trim() || null,
      unitQtyBase: unitQty,
      supplierSku: (sku && sku.value || '').trim() || null
    });
    showToast('Lieferant zugeordnet', 'success');
    await loadSuppliersSection(_draft.id);
  } catch (e) {
    console.error('[ingredients] supplier add:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function ingredientSupplierRemove(id) {
  if (!_draft || !_draft.id) return;
  try {
    await storage().ingredientSuppliers.remove(id);
    await loadSuppliersSection(_draft.id);
  } catch (e) {
    console.error('[ingredients] supplier remove:', e);
    showToast('Entfernen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function ingredientSupplierSetPrimary(id) {
  if (!_draft || !_draft.id) return;
  try {
    await storage().ingredientSuppliers.setPrimary(_draft.id, id);
    showToast('Hauptlieferant gesetzt — Einkaufspreis gesynct', 'success');
    await loadSuppliersSection(_draft.id);
  } catch (e) {
    console.error('[ingredients] set primary:', e);
    showToast('Setzen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// Draft-Handler (kein Re-Render → non-destruktiv)
// -----------------------------------------------------------------------------
export function ingredientDraftSet(field, value) { if (_draft) _draft[field] = value; }
export function ingredientDraftSetNum(field, value) {
  if (!_draft) return;
  _draft[field] = (value === '' || value == null) ? null : Number(value);
}
export function ingredientDraftSetBaseUnit(value) {
  if (_draft) _draft.baseUnit = value;
  const row = document.getElementById('ing-piece-row');
  if (row) row.style.display = value === 'piece' ? '' : 'none';
}
export function ingredientDraftToggle(field, key, on) {
  if (!_draft) return;
  const set = new Set(_draft[field] || []);
  if (on) set.add(key); else set.delete(key);
  _draft[field] = [...set];
}
export function ingredientAddUnit() {
  const nameEl = document.getElementById('ing-unit-name');
  const facEl = document.getElementById('ing-unit-factor');
  const name = (nameEl && nameEl.value || '').trim();
  const factor = Number(facEl && facEl.value);
  if (!name || !isFinite(factor) || factor <= 0) { showToast('Name + positiver Faktor nötig', 'error'); return; }
  if (_draftUnits.some(u => u.unitName.toLowerCase() === name.toLowerCase())) { showToast('Einheit existiert schon', 'error'); return; }
  _draftUnits.push({ unitName: name, gramsOrMl: factor, _new: true });
  if (nameEl) nameEl.value = ''; if (facEl) facEl.value = '';
  renderUnitsList();
}
export function ingredientRemoveUnit(idx) {
  const u = _draftUnits[idx];
  if (!u) return;
  if (u.id) _draftUnits[idx]._deleted = true; // bestehende → beim Save löschen
  _draftUnits.splice(idx, 1);
  if (u.id) _pendingUnitDeletes.push(u.id);
  renderUnitsList();
}
let _pendingUnitDeletes = [];

// -----------------------------------------------------------------------------
// Speichern / Löschen
// -----------------------------------------------------------------------------
export async function saveIngredient() {
  if (!_draft) return;
  if (!_draft.name || !_draft.name.trim()) { showToast('Name ist Pflicht', 'error'); return; }
  if (_draft.baseUnit === 'piece' && _draft.pieceWeightG == null) {
    showToast('Bei „Stück" bitte das Stück-Gewicht (g) angeben', 'error'); return;
  }
  // E4a-Hotfix — bei Hauptlieferant die abgeleiteten Einkaufsfelder NICHT zurückschreiben
  // (sonst clobbert das manuelle Formular den Sync-Trigger). Server hat zusätzlich einen
  // Guard-Trigger (phase_121b); hier client-seitig sauber halten + unnötige Writes sparen.
  const hasPrimary = !!primarySupplier(_draftSuppliers);
  const payload = omitDerivedPurchase(_draft, hasPrimary);
  try {
    let saved;
    if (_draft.id) {
      saved = await storage().ingredients.update(_draft.id, payload);
    } else {
      saved = await storage().ingredients.create(payload);
    }
    // benannte Einheiten persistieren (neue anlegen, markierte löschen)
    for (const uid of _pendingUnitDeletes) {
      try { await storage().ingredientUnits.remove(uid); } catch (_) {}
    }
    _pendingUnitDeletes = [];
    for (const u of _draftUnits) {
      if (u._new && !u.id) {
        try { await storage().ingredientUnits.create({ ingredientId: saved.id, unitName: u.unitName, gramsOrMl: u.gramsOrMl }); } catch (e) { console.warn('[ingredients] unit create:', e); }
      }
    }
    showToast('Zutat gespeichert', 'success');
    if (typeof window.closeModal === 'function') window.closeModal();
    await renderIngredientsAdmin();
  } catch (e) {
    console.error('[ingredients] save:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function deleteIngredient(id) {
  const it = _cache.find(x => x.id === id);
  if (!(await window.styledConfirm({ title: `Zutat „${it ? it.name : id}" löschen?`, body: 'Soft-Delete — Rezepte, die sie nutzen, zeigen dann „unvollständig".', danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  try {
    await storage().ingredients.remove(id);
    showToast('Zutat gelöscht', 'success');
    await renderIngredientsAdmin();
  } catch (e) {
    console.error('[ingredients] delete:', e);
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// window-Bindings (CSP: Inline-Handler ohne window.*)
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderIngredientsAdmin, openIngredientForm,
    ingredientDraftSet, ingredientDraftSetNum, ingredientDraftSetBaseUnit, ingredientDraftToggle,
    ingredientAddUnit, ingredientRemoveUnit, saveIngredient, deleteIngredient,
    ingredientSupplierAdd, ingredientSupplierRemove, ingredientSupplierSetPrimary
  });
}
